import * as React from 'react';
import type { Editor } from '@tiptap/react';
// --- Lib ---
import { getAvailableSlashCommandConfigurations } from '../../../../app/rte/lib/slash-command-utils';

// --- Tiptap UI ---
import type { SuggestionItem } from '../../tiptap-ui-utils/suggestion-menu';

export interface SlashMenuConfig {
  customItems?: SuggestionItem[];
  itemGroups?: Record<string, string>;
  showGroups?: boolean;
}

// Removed hardcoded texts - now using standardized slash command configurations

// Removed getItemImplementations - now using standardized slash command configurations

function organizeItemsByGroups(
  items: SuggestionItem[],
  showGroups: boolean
): SuggestionItem[] {
  if (!showGroups) {
    return items.map((item) => ({ ...item, group: '' }));
  }

  const groups: { [groupLabel: string]: SuggestionItem[] } = {};

  // Group items
  items.forEach((item) => {
    const groupLabel = item.group || '';
    if (!groups[groupLabel]) {
      groups[groupLabel] = [];
    }
    groups[groupLabel].push(item);
  });

  // Flatten groups in order (this maintains the visual order for keyboard navigation)
  const organizedItems: SuggestionItem[] = [];
  Object.entries(groups).forEach(([, groupItems]) => {
    organizedItems.push(...groupItems);
  });

  return organizedItems;
}

/**
 * Custom hook for slash dropdown menu functionality
 */
export function useSlashDropdownMenu(config?: SlashMenuConfig) {
  const getSlashMenuItems = React.useCallback(
    (editor: Editor) => {
      const items: SuggestionItem[] = [];

      // Get standardized slash command configurations
      const slashConfigs = getAvailableSlashCommandConfigurations(editor);

      // Convert slash commands to suggestion items
      slashConfigs.forEach((slashConfig) => {
        const item: SuggestionItem = {
          onSelect: ({ editor }) => slashConfig.action({ editor }),
          title: slashConfig.title,
          subtext: slashConfig.subtext,
          keywords: slashConfig.keywords,
          badge: slashConfig.badge,
          group: slashConfig.group,
        };

        // Apply custom group override if provided
        if (config?.itemGroups) {
          const groupOverride = Object.entries(config.itemGroups).find(
            ([key]) =>
              slashConfig.title.toLowerCase().includes(key.toLowerCase()) ||
              slashConfig.keywords.some((keyword) =>
                keyword.toLowerCase().includes(key.toLowerCase())
              )
          );
          if (groupOverride) {
            item.group = groupOverride[1];
          }
        }

        // If groups are disabled, clear the group
        if (config?.showGroups === false) {
          item.group = '';
        }

        items.push(item);
      });

      // Add custom items if provided
      if (config?.customItems) {
        items.push(...config.customItems);
      }

      // Reorganize items by groups to ensure keyboard navigation works correctly
      return organizeItemsByGroups(items, config?.showGroups !== false);
    },
    [config]
  );

  return {
    getSlashMenuItems,
    config,
  };
}
